import { Vector2 } from "core/Maths/math.vector.pure";
import { Tools } from "core/Misc/tools.pure";
import { RegisterSceneLoaderPlugin, } from "core/Loading/sceneLoader";
import { AssetContainer } from "core/assetContainer";
import { OBJFileLoaderMetadata } from "./objFileLoader.metadata";
import { MTLFileLoader } from "./mtlFileLoader";
import { SolidParser } from "./solidParser";
import { StandardMaterial } from "core/Materials/standardMaterial.pure";
/**
 * OBJ file type loader.
 * This is a babylon scene loader plugin.
 */
export class OBJFileLoader {
    /**
     * Invert Y-Axis of referenced textures on load
     */
    static get INVERT_TEXTURE_Y() {
        return MTLFileLoader.INVERT_TEXTURE_Y;
    }
    static set INVERT_TEXTURE_Y(value) {
        MTLFileLoader.INVERT_TEXTURE_Y = value;
    }
    /**
     * Creates loader for .OBJ files
     *
     * @param loadingOptions options for loading and parsing OBJ/MTL files.
     */
    constructor(loadingOptions) {
        /**
         * Defines the name of the plugin.
         */
        this.name = OBJFileLoaderMetadata.name;
        /**
         * Defines the extension the plugin is able to load.
         */
        this.extensions = OBJFileLoaderMetadata.extensions;
        this._assetContainer = null;
        this._loadingOptions = { ...OBJFileLoader._DefaultLoadingOptions, ...(loadingOptions ?? {}) };
    }
    static get _DefaultLoadingOptions() {
        return {
            encoding: OBJFileLoader.ENCODING,
            computeNormals: OBJFileLoader.COMPUTE_NORMALS,
            optimizeNormals: OBJFileLoader.OPTIMIZE_NORMALS,
            importVertexColors: OBJFileLoader.IMPORT_VERTEX_COLORS,
            invertY: OBJFileLoader.INVERT_Y,
            invertTextureY: OBJFileLoader.INVERT_TEXTURE_Y,
            waitForTextures: OBJFileLoader.WAIT_FOR_TEXTURES,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            UVScaling: OBJFileLoader.UV_SCALING,
            materialLoadingFailsSilently: OBJFileLoader.MATERIAL_LOADING_FAILS_SILENTLY,
            optimizeWithUV: OBJFileLoader.OPTIMIZE_WITH_UV,
            skipMaterials: OBJFileLoader.SKIP_MATERIALS,
            useLegacyBehavior: OBJFileLoader.USE_LEGACY_BEHAVIOR,
        };
    }
    /**
     * Calls synchronously the MTL file attached to this obj.
     * Load function or importMesh function don't enable to load 2 files in the same time asynchronously.
     * Without this function materials are not displayed in the first frame (but displayed after).
     * In consequence it is impossible to get material information in your HTML file
     *
     * @param url The URL of the MTL file
     * @param rootUrl defines where to load data from
     * @param onSuccess Callback function to be called when the MTL file is loaded
     * @param onFailure
     */
    _loadMTL(url, rootUrl, onSuccess, onFailure) {
        //The complete path to the mtl file
        const pathOfFile = rootUrl + url;
        // Loads through the babylon tools to allow fileInput search.
        Tools.LoadFile(pathOfFile, onSuccess, undefined, undefined, true, (request, exception) => {
            onFailure(pathOfFile, exception);
        });
    }
    _decode(data) {
        if (typeof data === "string") {
            return data;
        }
        const bytes = new Uint8Array(data);
        const encoding = this._loadingOptions.encoding ?? "auto";
        if (encoding !== "auto") {
            return new TextDecoder(encoding).decode(bytes);
        }
        if (bytes[0] === 0xff && bytes[1] === 0xfe) {
            return new TextDecoder("utf-16le").decode(bytes);
        }
        if (bytes[0] === 0xfe && bytes[1] === 0xff) {
            return new TextDecoder("utf-16be").decode(bytes);
        }
        try {
            return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
        }
        catch {
            return new TextDecoder("gb18030").decode(bytes);
        }
    }
    /** @internal */
    createPlugin(options) {
        return new OBJFileLoader(options[OBJFileLoaderMetadata.name]);
    }
    /**
     * If the data string can be loaded directly.
     * @returns if the data can be loaded directly
     */
    canDirectLoad() {
        return false;
    }
    /**
     * Imports one or more meshes from the loaded OBJ data and adds them to the scene
     * @param meshesNames a string or array of strings of the mesh names that should be loaded from the file
     * @param scene the scene the meshes should be added to
     * @param data the OBJ data to load
     * @param rootUrl root url to load from
     * @returns a promise containing the loaded meshes, particles, skeletons and animations
     */
    // eslint-disable-next-line @typescript-eslint/promise-function-async, no-restricted-syntax
    importMeshAsync(meshesNames, scene, data, rootUrl) {
        //get the meshes from OBJ file
        // eslint-disable-next-line github/no-then
        return this._parseSolidAsync(meshesNames, scene, data, rootUrl).then((meshes) => {
            const geometries = [];
            for (let i = 0; i < meshes.length; ++i) {
                const geometry = meshes[i].geometry;
                if (geometry) {
                    geometries.push(geometry);
                }
            }
            return {
                meshes: meshes,
                particleSystems: [],
                skeletons: [],
                animationGroups: [],
                transformNodes: [],
                geometries: geometries,
                lights: [],
                spriteManagers: [],
            };
        });
    }
    /**
     * Imports all objects from the loaded OBJ data and adds them to the scene
     * @param scene the scene the objects should be added to
     * @param data the OBJ data to load
     * @param rootUrl root url to load from
     * @returns a promise which completes when objects have been loaded to the scene
     */
    // eslint-disable-next-line no-restricted-syntax
    loadAsync(scene, data, rootUrl) {
        //Get the 3D model
        // eslint-disable-next-line github/no-then
        return this.importMeshAsync(null, scene, data, rootUrl).then(() => {
            // return void
        });
    }
    /**
     * Load into an asset container.
     * @param scene The scene to load into
     * @param data The data to import
     * @param rootUrl The root url for scene and resources
     * @returns The loaded asset container
     */
    async loadAssetContainerAsync(scene, data, rootUrl) {
        const container = new AssetContainer(scene);
        this._assetContainer = container;
        try {
            // Resources are collected as they are created, including before texture loading settles.
            await this.importMeshAsync(null, scene, data, rootUrl);
            return container;
        }
        catch (ex) {
            container.dispose();
            throw ex;
        }
        finally {
            this._assetContainer = null;
        }
    }
    /**
     * Read the OBJ file and create an Array of meshes.
     * Each mesh contains all information given by the OBJ and the MTL file.
     * i.e. vertices positions and indices, optional normals values, optional UV values, optional material
     * @param meshesNames defines a string or array of strings of the mesh names that should be loaded from the file
     * @param scene defines the scene where are displayed the data
     * @param data defines the content of the obj file
     * @param rootUrl defines the path to the folder
     * @returns the list of loaded meshes
     */
    // eslint-disable-next-line @typescript-eslint/promise-function-async, no-restricted-syntax
    _parseSolidAsync(meshesNames, scene, data, rootUrl) {
        let fileToLoad = ""; //The name of the mtlFile to load
        const materialsFromMTLFile = new MTLFileLoader();
        const materialToUse = [];
        const babylonMeshesArray = []; //The mesh for babylon
        data = this._decode(data);
        // Sanitize data
        data = data.replace(/#.*$/gm, "").trim();
        // Main function
        const solidParser = new SolidParser(materialToUse, babylonMeshesArray, this._loadingOptions);
        solidParser.parse(meshesNames, data, scene, this._assetContainer, (fileName) => {
            fileToLoad = fileName;
        });
        // load the materials
        const mtlPromises = [];
        // Check if we have a file to load
        if (fileToLoad !== "" && !this._loadingOptions.skipMaterials) {
            // Load the MTL and optionally wait for the textures used by the selected meshes.
            mtlPromises.push(new Promise((resolve, reject) => {
                this._loadMTL(fileToLoad, rootUrl, async (dataLoaded) => {
                    try {
                        //Create materials thanks MTLLoader function
                        const textureLoadPromises = materialsFromMTLFile.parseMTL(scene, this._decode(dataLoaded), rootUrl, this._assetContainer, this._loadingOptions.invertTextureY, new Set(materialToUse), this._loadingOptions.waitForTextures);
                        // Parsing creates the selected materials synchronously; only their textures are pending.
                        for (let n = 0; n < materialsFromMTLFile.materials.length; ++n) {
                            const material = materialsFromMTLFile.materials[n];
                            for (let index = materialToUse.indexOf(material.name); index !== -1; index = materialToUse.indexOf(material.name, index + 1)) {
                                const mesh = babylonMeshesArray[index];
                                mesh.material = material;
                                if (!mesh.getTotalIndices()) {
                                    // No indices, we need to turn on point cloud
                                    material.pointsCloud = true;
                                }
                            }
                        }
                        if (this._loadingOptions.waitForTextures) {
                            const results = await Promise.allSettled(textureLoadPromises);
                            const rejected = results.find((result) => result.status === "rejected");
                            if (rejected) {
                                throw rejected.reason;
                            }
                        }
                        resolve();
                    }
                    catch (e) {
                        Tools.Warn(`Error processing MTL file: '${fileToLoad}'`);
                        if (this._loadingOptions.materialLoadingFailsSilently) {
                            resolve();
                        }
                        else {
                            // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                            reject(e);
                        }
                    }
                }, (pathOfFile, exception) => {
                    Tools.Warn(`Error downloading MTL file: '${fileToLoad}'`);
                    if (this._loadingOptions.materialLoadingFailsSilently) {
                        resolve();
                    }
                    else {
                        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                        reject(exception);
                    }
                });
            }));
        }
        //Return an array with all Mesh
        // eslint-disable-next-line github/no-then
        return Promise.all(mtlPromises).then(() => {
            const isLine = (mesh) => Boolean(mesh._internalMetadata?.["_isLine"] ?? false);
            // Iterate over the mesh, determine if it is a line mesh, clone or modify the material to line rendering.
            babylonMeshesArray.forEach((mesh) => {
                if (isLine(mesh)) {
                    let mat = mesh.material;
                    if (!mat) {
                        const blockEntityCollection = scene._blockEntityCollection;
                        scene._blockEntityCollection = !!this._assetContainer;
                        try {
                            mat = new StandardMaterial(mesh.name + "_line", scene);
                            mat._parentContainer = this._assetContainer;
                            this._assetContainer?.materials.push(mat);
                        }
                        finally {
                            scene._blockEntityCollection = blockEntityCollection;
                        }
                    }
                    // If another mesh is using this material and it is not a line then we need to clone it.
                    const needClone = mat.getBindedMeshes().filter((e) => !isLine(e)).length > 0;
                    if (needClone) {
                        // Clone only the material; keep the MTL texture objects shared.
                        const sourceMaterial = mat;
                        const { ambientTexture, diffuseTexture, specularTexture, bumpTexture, opacityTexture } = sourceMaterial;
                        const blockEntityCollection = scene._blockEntityCollection;
                        let lineMaterial;
                        try {
                            sourceMaterial.ambientTexture = null;
                            sourceMaterial.diffuseTexture = null;
                            sourceMaterial.specularTexture = null;
                            sourceMaterial.bumpTexture = null;
                            sourceMaterial.opacityTexture = null;
                            scene._blockEntityCollection = !!this._assetContainer;
                            lineMaterial = sourceMaterial.clone(sourceMaterial.name + "_line");
                            lineMaterial._parentContainer = this._assetContainer;
                            this._assetContainer?.materials.push(lineMaterial);
                        }
                        finally {
                            scene._blockEntityCollection = blockEntityCollection;
                            sourceMaterial.ambientTexture = ambientTexture;
                            sourceMaterial.diffuseTexture = diffuseTexture;
                            sourceMaterial.specularTexture = specularTexture;
                            sourceMaterial.bumpTexture = bumpTexture;
                            sourceMaterial.opacityTexture = opacityTexture;
                        }
                        lineMaterial.ambientTexture = ambientTexture;
                        lineMaterial.diffuseTexture = diffuseTexture;
                        lineMaterial.specularTexture = specularTexture;
                        lineMaterial.bumpTexture = bumpTexture;
                        lineMaterial.opacityTexture = opacityTexture;
                        mat = lineMaterial;
                    }
                    mat.wireframe = true;
                    mesh.material = mat;
                }
            });
            babylonMeshesArray.forEach((mesh) => {
                if (mesh._internalMetadata) {
                    mesh._internalMetadata["_isLine"] = undefined;
                }
            });
            return babylonMeshesArray;
        });
    }
}
/**
 * Defines the character encoding used to decode OBJ and MTL files.
 */
OBJFileLoader.ENCODING = "auto";
/**
 * Defines if UVs are optimized by default during load.
 */
OBJFileLoader.OPTIMIZE_WITH_UV = true;
/**
 * Invert model on y-axis (does a model scaling inversion)
 */
OBJFileLoader.INVERT_Y = false;
/**
 * Wait for referenced textures to finish loading before completing the OBJ load.
 * Defaults to false for backwards compatibility.
 */
OBJFileLoader.WAIT_FOR_TEXTURES = false;
/**
 * Include in meshes the vertex colors available in some OBJ files.  This is not part of OBJ standard.
 */
OBJFileLoader.IMPORT_VERTEX_COLORS = false;
/**
 * Compute the normals for the model, even if normals are present in the file.
 */
OBJFileLoader.COMPUTE_NORMALS = false;
/**
 * Optimize the normals for the model. Lighting can be uneven if you use OptimizeWithUV = true because new vertices can be created for the same location if they pertain to different faces.
 * Using OptimizehNormals = true will help smoothing the lighting by averaging the normals of those vertices.
 */
OBJFileLoader.OPTIMIZE_NORMALS = false;
/**
 * Defines custom scaling of UV coordinates of loaded meshes.
 */
OBJFileLoader.UV_SCALING = new Vector2(1, 1);
/**
 * Skip loading the materials even if defined in the OBJ file (materials are ignored).
 */
OBJFileLoader.SKIP_MATERIALS = false;
/**
 * When a material fails to load OBJ loader will silently fail and onSuccess() callback will be triggered.
 *
 * Defaults to true for backwards compatibility.
 */
OBJFileLoader.MATERIAL_LOADING_FAILS_SILENTLY = true;
/**
 * Loads assets without handedness conversions. This flag is for compatibility. Use it only if absolutely required. Defaults to false.
 */
OBJFileLoader.USE_LEGACY_BEHAVIOR = false;
//Add this loader into the register plugin
let _Registered = false;
/**
 * Registers the OBJFileLoader scene loader plugin.
 * Safe to call multiple times; only the first call has an effect.
 */
export function RegisterOBJFileLoader() {
    if (_Registered) {
        return;
    }
    _Registered = true;
    RegisterSceneLoaderPlugin(new OBJFileLoader());
}
//# sourceMappingURL=objFileLoader.pure.js.map